import React, { useRef, useState, useEffect } from 'react';
// Import your SVG directly
import totalAgeSvg from '../assets/total_age.svg';

const HorizontalScroll = () => {
    const wrapperRef = useRef(null);
    const trackRef = useRef(null);
    const revealRef = useRef(null);
    const [isMobile, setIsMobile] = useState(false);
    // Measured rather than 100vh so the mobile URL bar showing/hiding doesn't resize the pinned area.
    const [viewportHeight, setViewportHeight] = useState(
        typeof window !== 'undefined' ? window.innerHeight : 0
    );
    const [isAtEnd, setIsAtEnd] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [noticeExpired, setNoticeExpired] = useState(false);
    // How far the image extends past the viewport; measured, since its width depends on its aspect ratio.
    const [overflowWidth, setOverflowWidth] = useState(0);

    const measureOverflow = () => {
        const wrapper = wrapperRef.current;
        const track = trackRef.current;
        if (!wrapper || !track) return 0;

        // The SVG artboard (0 0 1774 304.7) has empty space past the last drawn element, so the pan
        // stops at the end of the artwork plus a small gap rather than at the artboard edge.
        const contentWidth = track.offsetWidth * 0.875;
        const trailingGap = Math.min(40, wrapper.clientWidth * 0.06);
        const overflow = Math.max(0, contentWidth + trailingGap - wrapper.clientWidth);
        setOverflowWidth(overflow);
        return overflow;
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            setViewportHeight(window.innerHeight);
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    // Vertical scroll budget dedicated to the horizontal pan.
    const scrollLength = Math.max(viewportHeight, overflowWidth * (isMobile ? 1.6 : 1.2));

    useEffect(() => {
        const wrapper = wrapperRef.current;
        const track = trackRef.current;
        if (!wrapper || !track) return;

        let frame = null;

        const render = () => {
            frame = null;

            const rect = wrapper.getBoundingClientRect();
            const distance = rect.height - viewportHeight;
            const progress = distance > 0
                ? Math.min(1, Math.max(0, -rect.top / distance))
                : 0;

            const maxTranslate = measureOverflow();
            track.style.transform = `translate3d(${-progress * maxTranslate}px, 0, 0)`;

            if (revealRef.current) {
                revealRef.current.style.clipPath = `inset(0 ${(1 - progress) * 100}% 0 0)`;
            }

            setIsAtEnd(progress >= 0.99);
            if (progress > 0.01) setHasStarted(true);
        };

        const onScroll = () => {
            if (frame === null) {
                frame = window.requestAnimationFrame(render);
            }
        };

        render();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (frame !== null) window.cancelAnimationFrame(frame);
        };
    }, [viewportHeight, overflowWidth]);

    useEffect(() => {
        if (!hasStarted) return;

        const timer = window.setTimeout(() => setNoticeExpired(true), 2000);
        return () => window.clearTimeout(timer);
    }, [hasStarted]);

    const imageStyle = {
        height: '85%',
        width: 'auto',
        objectFit: 'contain',
        userSelect: 'none',
        pointerEvents: 'none'
    };

    const overlayStyle = {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start'
    };

    return (
        <div
            ref={wrapperRef}
            style={{
                position: 'relative',
                width: '100%',
                height: viewportHeight ? `${viewportHeight + scrollLength}px` : '300vh'
            }}
        >
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    height: viewportHeight ? `${viewportHeight}px` : '100vh',
                    width: '100%',
                    overflow: 'hidden'
                }}
            >
                <div
                    ref={trackRef}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        width: 'max-content',
                        minWidth: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        willChange: 'transform'
                    }}
                >
                    {/* Background grayscale image, also what gives the track its width */}
                    <img
                        src={totalAgeSvg}
                        alt="placeholder background"
                        onLoad={measureOverflow}
                        style={{ ...imageStyle, filter: 'grayscale(100%) opacity(40%)' }}
                    />

                    {/* Revealing image */}
                    <div
                        ref={revealRef}
                        style={{ ...overlayStyle, clipPath: 'inset(0 100% 0 0)' }}
                    >
                        <img src={totalAgeSvg} alt="main content" style={imageStyle} />
                    </div>
                </div>

                {/* Scroll indicator */}
                <div style={{
                    position: 'absolute',
                    bottom: '80px',
                    left: 0,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    opacity: isAtEnd || noticeExpired ? 0 : 1,
                    transition: 'opacity 0.5s ease',
                    pointerEvents: 'none',
                    zIndex: 15
                }}>
                    <div style={{
                        padding: '10px 20px',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        borderRadius: '30px',
                        fontSize: '14px'
                    }}>
                        Scroll to continue
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HorizontalScroll;
